import React, { useEffect, useState } from "react";
import "./CartItem.css";
import { toast } from "react-toastify";

const CartItem = ({ item, onIncrease, onDecrease, onRemove, onDiscountChange }) => {
  const [couponCode, setCouponCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [couponOpen, setCouponOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Presentation only. The row is not unmounted here -- the parent still owns
  // removal and still decides when the item leaves the list. This flag only
  // lets the row dim and drift while the delete request is in flight, so the
  // removal reads as an action rather than a disappearance. It never hides
  // the row: if the request fails the parent keeps the item, the flag is
  // still true and the row stays legible at 0.3 opacity instead of blank.
  const [removing, setRemoving] = useState(false);

  const discountedPrice = (item.price - discountAmount) * item.quantity;
  const originalPrice = item.price * item.quantity;
  const hasCoupon = discountAmount > 0;

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(`coupon_${item._id}_${item.size}`));
    if (saved) {
      setCouponCode(saved.code);
      setDiscountAmount(saved.discount);
      onDiscountChange(item._id, item.size, saved.discount, saved.code);
    }
  }, []);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setLoading(true);
    try {
      const res = await fetch("https://riymay.com/api/v1/user/product/coupon/validator", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ couponCode, productId: item._id }),
      });
      const result = await res.json();
      if (!res.ok) {
        toast.warning("Invalid coupon code.", { position: "top-right", autoClose: 3000, toastId: "coupon-invalid", className: "custom-toast", bodyClassName: "custom-toast-body" });
        return;
      }
      toast.success(result.reason, { position: "top-right", autoClose: 3000, toastId: "coupon-ok", className: "custom-toast", bodyClassName: "custom-toast-body" });
      setDiscountAmount(result.discountAmount);
      onDiscountChange(item._id, item.size, result.discountAmount, couponCode);
      localStorage.setItem(`coupon_${item._id}_${item.size}`, JSON.stringify({ code: couponCode, discount: result.discountAmount }));
      setCouponOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const removeCoupon = async () => {
    try {
      await fetch(`https://riymay.com/api/v1/user/product/coupon/validator/${item._id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          "Content-Type": "application/json",
        },
      });
      setCouponCode("");
      setDiscountAmount(0);
      onDiscountChange(item._id, item.size, 0, "");
      localStorage.removeItem(`coupon_${item._id}_${item.size}`);
      toast.warning("Coupon removed.", { position: "top-right", autoClose: 2000, className: "custom-toast", bodyClassName: "custom-toast-body" });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className={`ci-item${removing ? " is-removing" : ""}`}>
      <div className="ci-thumb">
        <img src={`https://riymay.com/assets/${item.images[0]?.url}`} alt={item.name} />
      </div>

      <div className="ci-body">
        <div className="ci-top">
          <div className="ci-info">
            <h4 className="ci-name">{item.name.substring(0, 28)}{item.name.length > 28 ? "…" : ""}</h4>
            <div className="ci-meta">
              <span
                className="ci-color-dot"
                style={{ background: item.color || "#556b2f" }}
              />
              <span>{item.color}</span>
              <span className="ci-sep">·</span>
              <span>{item.size}</span>
              <span className="ci-sep">·</span>
              <span className={item.stockStatus ? "ci-stock-ok" : "ci-stock-no"}>
                {item.stockStatus ? "In Stock" : "Out of Stock"}
              </span>
            </div>
          </div>

          <div className="ci-right">
            {hasCoupon ? (
              <>
                <span className="ci-price-struck" key={`was-${originalPrice}`}>₹{originalPrice.toFixed(0)}</span>
                <span className="ci-price" key={`now-${discountedPrice}`}>₹{discountedPrice.toFixed(0)}</span>
              </>
            ) : (
              <span className="ci-price" key={`now-${originalPrice}`}>₹{originalPrice.toFixed(0)}</span>
            )}
            <div className="ci-qty">
              <button className="ci-qbtn" onClick={() => onDecrease(item._id, item.size, item.color)} disabled={item.quantity <= 1}>−</button>
              {/* Keyed on the value: React remounts the span when the count changes,
                  which replays the CSS enter animation. Pure CSS from there -- no
                  timer, no rAF, and the number is present in the DOM either way. */}
              <span className="ci-qnum" key={item.quantity}>{item.quantity}</span>
              <button className="ci-qbtn" onClick={() => onIncrease(item._id, item.size, item.color)}>+</button>
            </div>
            <button
              className="ci-remove"
              onClick={() => {
                setRemoving(true);
                onRemove(item._id, item.size, item.color);
              }}
            >Remove</button>
          </div>
        </div>

        <div className="ci-coupon-area">
          {hasCoupon ? (
            <div className="ci-coupon-applied">
              <span className="ci-coupon-chip">{couponCode}</span>
              <span className="ci-coupon-saving">−₹{(discountAmount * item.quantity).toFixed(0)} saved</span>
              <button className="ci-coupon-remove" onClick={removeCoupon}>Remove</button>
            </div>
          ) : (
            <>
              <button
                className={`ci-coupon-toggle ${couponOpen ? "open" : ""}`}
                onClick={() => setCouponOpen(!couponOpen)}
              >
                <span className="ci-toggle-arrow">▶</span> Have a coupon?
              </button>
              {couponOpen && (
                <div className="ci-coupon-field">
                  <input
                    className="ci-cin"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                  />
                  <button className="ci-capply" onClick={applyCoupon} disabled={loading}>
                    {loading ? "…" : "Apply"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartItem;
